import { replaceNullsWithUndefined } from "lib/permissions/helpers"
import { IconButton, Stack, TableCell, TableRow } from "@mui/material"
import { UserAvatar } from "minimals-template/components/user-avatar"
import noop from "lib/noop"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { BlockClicks } from "lib/@components/block-clicks"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { AuthoringUserWorkPackageCell } from "./authoring-user-work-package-cell"
import { AuthoringUserMultiSelectCell } from "./authoring-user-multi-select-cell"

export function AuthoringUserRow({
    item,
    selected,
    onSelectRow = noop,
    onUpdatePermissions = noop,
    onUpdateWorkPackages,
    onRemove,
    roleSelector = true,
    workPackageSelector = false,
}) {
    const user = item?.node ?? item
    const { displayName: name, email, company } = replaceNullsWithUndefined(user?.metadata) ?? {}
    const { roles } = useBoundContext()
    const userRoles = user?.projectRoles || user?.metadata?.roles || []
    const activeCols = 1 + (roleSelector ? 1 : 0) + (workPackageSelector ? 1 : 0)
    const colWidth = activeCols === 3 ? "20%" : activeCols === 2 ? "25%" : "33%"

    if (!user) return null

    const isSelected = !!(selected && user?.id && selected.id === user.id)

    return (
        <TableRow
            sx={{ cursor: "pointer" }}
            hover
            selected={isSelected}
            onClick={() => onSelectRow(user)}
            data-cy={`authoring-user-row-${email}`}
        >
            <TableCell sx={{ width: colWidth, maxWidth: { xs: 150, md: 300 } }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <UserAvatar user={user} className="permission-user-avatar" />
                    <TruncatedTypography variant="subtitle2" data-cy={`permission-user-name`} noWrap>
                        {name}
                    </TruncatedTypography>
                </Stack>
            </TableCell>
            <TableCell align="left" sx={{ width: colWidth, maxWidth: { xs: 150, md: 300 } }}>
                <TruncatedTypography data-cy="permission-email" noWrap variant="subtitle2">
                    {email}
                </TruncatedTypography>
            </TableCell>
            <TableCell align="left" sx={{ width: colWidth }}>
                <TruncatedTypography data-cy="permission-project" noWrap variant="subtitle2">
                    {company}
                </TruncatedTypography>
            </TableCell>

            {roleSelector && (
                <AuthoringUserMultiSelectCell
                    label="Role"
                    options={roles ?? []}
                    selectedIds={userRoles}
                    onToggle={handleRoleToggle}
                    dataCy="permission-user-role-selection-combo-selector"
                    inputId={`role-select-${user.id}`}
                    className="role-select"
                />
            )}

            {workPackageSelector && (
                <AuthoringUserWorkPackageCell user={user} onUpdateWorkPackages={onUpdateWorkPackages} />
            )}

            {onRemove && (
                <TableCell align="right">
                    <BlockClicks>
                        <IconButton
                            size="medium"
                            color="error.main"
                            onClick={() => onRemove(user)}
                            aria-label={`Remove ${name}`}
                            title={`Remove ${name}`}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "error.secondary",
                                    color: "error.dark",
                                },
                            }}
                            data-cy={`remove-${email}`}
                        >
                            <Iconify icon="mdi:delete" aria-hidden="true" />
                        </IconButton>
                    </BlockClicks>
                </TableCell>
            )}
        </TableRow>
    )

    async function handleRoleToggle(role) {
        const updatedRoles = toggleId(userRoles || [], role.id)
        await onUpdatePermissions(user, updatedRoles)
    }
}

function toggleId(array, id) {
    if (array.includes(id)) {
        return array.filter((element) => element !== id)
    }
    return [...array, id]
}
